import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import type { RefCallback } from "react";
import { useTranslation } from "react-i18next";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import styles from "@/components/lms/lmsCustomIntervalFields.module.css";
import LmsIntervalFields from "@/components/lms/lmsIntervalFields";

export interface LmsCustomIntervalState {
  useCustomIntervals: boolean;
  completionIntervalInDays: string;
  validityIntervalInDays: string;
}

export const getDefaultCustomIntervalState = (): LmsCustomIntervalState => ({
  useCustomIntervals: false,
  completionIntervalInDays: "",
  validityIntervalInDays: "",
});

const isFilled = (value: string) => value.trim() !== "";

export const isValidCustomIntervalValue = (value: string) => {
  if (!isFilled(value)) return true;
  const numericValue = Number(value);
  return Number.isSafeInteger(numericValue) && numericValue >= 0;
};

export const canSubmitCustomIntervals = ({
  useCustomIntervals,
  completionIntervalInDays,
  validityIntervalInDays,
}: LmsCustomIntervalState) => {
  if (!useCustomIntervals) return true;

  return (
    (isFilled(completionIntervalInDays) || isFilled(validityIntervalInDays)) &&
    isValidCustomIntervalValue(completionIntervalInDays) &&
    isValidCustomIntervalValue(validityIntervalInDays)
  );
};

export type LmsCustomIntervalValidationTarget =
  | "completionInterval"
  | "validityInterval";

export const getInvalidCustomIntervalTargets = ({
  useCustomIntervals,
  completionIntervalInDays,
  validityIntervalInDays,
}: LmsCustomIntervalState): LmsCustomIntervalValidationTarget[] => {
  if (!useCustomIntervals) return [];

  if (
    !isFilled(completionIntervalInDays) &&
    !isFilled(validityIntervalInDays)
  ) {
    return ["completionInterval"];
  }

  const invalidTargets: LmsCustomIntervalValidationTarget[] = [];
  if (!isValidCustomIntervalValue(completionIntervalInDays)) {
    invalidTargets.push("completionInterval");
  }
  if (!isValidCustomIntervalValue(validityIntervalInDays)) {
    invalidTargets.push("validityInterval");
  }
  return invalidTargets;
};

export const buildCustomIntervalPayload = (
  {
    useCustomIntervals,
    completionIntervalInDays,
    validityIntervalInDays,
  }: LmsCustomIntervalState,
  options: { includeNullForEmpty: boolean } = { includeNullForEmpty: false }
) => {
  if (!useCustomIntervals) {
    return options.includeNullForEmpty
      ? {
          completion_interval_in_days: null,
          validity_interval_in_days: null,
        }
      : {};
  }

  const payload: Record<string, number | null> = {};

  if (isFilled(completionIntervalInDays)) {
    payload.completion_interval_in_days = Number(completionIntervalInDays);
  } else if (options.includeNullForEmpty) {
    payload.completion_interval_in_days = null;
  }

  if (isFilled(validityIntervalInDays)) {
    payload.validity_interval_in_days = Number(validityIntervalInDays);
  } else if (options.includeNullForEmpty) {
    payload.validity_interval_in_days = null;
  }

  return payload;
};

interface Props {
  value: LmsCustomIntervalState;
  onChange: (value: LmsCustomIntervalState) => void;
  invalidTargets?: readonly LmsCustomIntervalValidationTarget[];
  registerCompletionTarget?: RefCallback<HTMLElement>;
  registerValidityTarget?: RefCallback<HTMLElement>;
}

export default function LmsCustomIntervalFields({
  value,
  onChange,
  invalidTargets = [],
  registerCompletionTarget,
  registerValidityTarget,
}: Props) {
  const { t } = useTranslation();
  const completionHasInvalidValue = !isValidCustomIntervalValue(
    value.completionIntervalInDays
  );
  const validityHasInvalidValue = !isValidCustomIntervalValue(
    value.validityIntervalInDays
  );
  const completionHasError =
    completionHasInvalidValue || invalidTargets.includes("completionInterval");
  const validityHasError =
    validityHasInvalidValue || invalidTargets.includes("validityInterval");

  const updateValue = (nextValue: Partial<LmsCustomIntervalState>) => {
    onChange({
      ...value,
      ...nextValue,
    });
  };

  return (
    <>
      <FormControlLabel
        label={t("set_custom_intervals")}
        control={
          <Checkbox
            checked={value.useCustomIntervals}
            onChange={(event) =>
              updateValue({ useCustomIntervals: event.target.checked })
            }
            color="secondary"
          />
        }
      />
      {value.useCustomIntervals && (
        <Box className={styles.intervalFieldsWrapper}>
          <DefaultInput
            ref={registerCompletionTarget}
            label={t("completion_interval_in_days")}
            name="lms-completion-interval"
            type="number"
            value={value.completionIntervalInDays}
            onChange={(event) =>
              updateValue({ completionIntervalInDays: event.target.value })
            }
            width="100%"
            inputStyle={{
              borderColor: completionHasError ? "red" : undefined,
            }}
          >
            {completionHasError && (
              <Typography
                className={styles.errorMessage}
                component="span"
                role="alert"
              >
                {completionHasInvalidValue
                  ? t("field_must_be_zero_or_greater")
                  : t("field_required")}
              </Typography>
            )}
          </DefaultInput>
          <Box ref={registerValidityTarget}>
            <LmsIntervalFields
              value={value.validityIntervalInDays}
              onValueChange={(nextValue) =>
                updateValue({ validityIntervalInDays: nextValue })
              }
              labelKey="validity_interval_in_days"
              helpKey="validity_interval_help"
              errorMessage={
                validityHasError
                  ? t("field_must_be_zero_or_greater")
                  : undefined
              }
            />
          </Box>
        </Box>
      )}
    </>
  );
}
